import { debounce, fetchConfig } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

(function initAmexCartSync() {
  const dataEl = document.getElementById('amex-cart-attributes-data');
  if (!dataEl) return;

  /** @type {{ targetCampaignId?: string, targetTierPublicId?: string, currentCampaignId?: string, currentTierPublicId?: string, needsSync?: boolean }} */
  let config;
  try {
    config = JSON.parse(dataEl.textContent || '{}');
  } catch (error) {
    console.error('Failed to parse amex-cart-attributes-data:', error);
    return;
  }

  const targetCampaignId = config.targetCampaignId ?? '';
  const targetTierPublicId = config.targetTierPublicId ?? '';
  let currentCampaignId = config.currentCampaignId ?? '';
  let currentTierPublicId = config.currentTierPublicId ?? '';
  let isSyncing = false;

  function isOutOfSync() {
    return currentCampaignId !== targetCampaignId || currentTierPublicId !== targetTierPublicId;
  }

  async function syncAttributes(force = false) {
    if (isSyncing) return;
    if (!force && !isOutOfSync()) return;

    isSyncing = true;
    try {
      const url = window.Theme?.routes?.cart_update_url || '/cart/update.js';
      const body = JSON.stringify({
        attributes: {
          amex_campaign_id: targetCampaignId,
          amex_tier_public_id: targetTierPublicId,
        },
      });

      const response = await fetch(url, fetchConfig('json', { body }));
      if (response.ok) {
        const updatedCart = await response.json();
        currentCampaignId = updatedCart?.attributes?.amex_campaign_id ?? targetCampaignId;
        currentTierPublicId = updatedCart?.attributes?.amex_tier_public_id ?? targetTierPublicId;
      } else {
        console.error('Failed to update Amex cart attributes:', response.statusText);
      }
    } catch (error) {
      console.error('Error during Amex cart attributes sync:', error);
    } finally {
      isSyncing = false;
    }
  }

  const debouncedSync = debounce(() => syncAttributes(), 300);

  /**
   * Dynamically injects or updates hidden inputs in cart and product forms
   * @param {HTMLFormElement} form
   */
  function injectInputsIntoForm(form) {
    if (!form || !(form instanceof HTMLFormElement)) return;

    const isCartForm = form.id === 'cart-form' || form.classList.contains('cart-form');
    const isProductForm =
      form.getAttribute('action')?.includes('/cart/add') || form.getAttribute('data-type') === 'add-to-cart-form';

    if (!isCartForm && !isProductForm) return;

    let campaignInput = form.querySelector('input[name="attributes[amex_campaign_id]"]');
    if (!campaignInput) {
      campaignInput = document.createElement('input');
      campaignInput.type = 'hidden';
      campaignInput.name = 'attributes[amex_campaign_id]';
      form.appendChild(campaignInput);
    }
    campaignInput.value = targetCampaignId;

    let tierInput = form.querySelector('input[name="attributes[amex_tier_public_id]"]');
    if (!tierInput) {
      tierInput = document.createElement('input');
      tierInput.type = 'hidden';
      tierInput.name = 'attributes[amex_tier_public_id]';
      form.appendChild(tierInput);
    }
    tierInput.value = targetTierPublicId;
  }

  function syncAllForms() {
    const forms = document.querySelectorAll(
      'form#cart-form, form.cart-form, form[action*="/cart/add"], form[data-type="add-to-cart-form"]'
    );
    forms.forEach((form) => {
      if (form instanceof HTMLFormElement) {
        injectInputsIntoForm(form);
      }
    });
  }

  function startSync() {
    syncAllForms();

    // 1. Initial Discrepancy Sync
    if (config.needsSync) {
      syncAttributes(true);
    }

    // 2. Observe dynamically added or morphed forms (modals, drawer re-renders)
    if (typeof MutationObserver === 'function') {
      const target = document.body || document.documentElement;
      if (target) {
        const observer = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            if (mutation.addedNodes.length > 0) {
              syncAllForms();
              break;
            }
          }
        });
        observer.observe(target, { childList: true, subtree: true });
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startSync);
  } else {
    startSync();
  }

  // 3. Intercept form submissions to ensure inputs are populated
  document.addEventListener(
    'submit',
    (event) => {
      const target = event.target;
      if (target && target instanceof HTMLFormElement) {
        injectInputsIntoForm(target);
      }
    },
    true
  );

  // 4. Global Storefront Event: shopify:cart:lines-update
  document.addEventListener('shopify:cart:lines-update', (event) => {
    if (event?.promise && typeof event.promise.then === 'function') {
      event.promise
        .then(({ cart }) => {
          if (cart?.attributes) {
            currentCampaignId = cart.attributes.amex_campaign_id ?? '';
            currentTierPublicId = cart.attributes.amex_tier_public_id ?? '';
            if (isOutOfSync()) {
              debouncedSync();
            }
          } else {
            debouncedSync();
          }
        })
        .catch((error) => {
          console.error('Error processing shopify:cart:lines-update in Amex sync:', error);
        });
    } else {
      debouncedSync();
    }
  });

  // 5. Theme Event: ThemeEvents.cartUpdate (cart:update)
  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    const cart = event?.detail?.resource;
    if (cart?.attributes) {
      currentCampaignId = cart.attributes.amex_campaign_id ?? '';
      currentTierPublicId = cart.attributes.amex_tier_public_id ?? '';
    }
    if (isOutOfSync()) {
      debouncedSync();
    }
  });

  // 6. Cart Drawer Interaction Fallback
  document.addEventListener('click', (event) => {
    const target = event.target;
    if (target && target instanceof Element) {
      if (target.closest('.cart-drawer, [aria-controls="cart-drawer"], [on\\:click*="/open"]')) {
        if (isOutOfSync()) {
          debouncedSync();
        }
      }
    }
  });
})();
